import { createRouter, createWebHistory } from 'vue-router';
import type { RouteRecordRaw } from 'vue-router';
import HomeView from '../views/HomeView.vue';
import LookupView from "@/views/LookupView.vue";
import AdminProfiles from "@/views/AdminProfiles.vue";
import RelationshipTypesView from "@/views/RelationshipTypesView.vue";
import SidePanelLayout from "@/layout/SidePanelLayout.vue";
import CompanyPageView from '@/views/CompanyPageView.vue';
import PersonPageView from '@/views/PersonPageView.vue';
import PersonGeneralData from '@/components/person/PersonGeneralData.vue';
import LoginView from '@/views/LoginView.vue';
import { requireAuth, requireGuest, requireSystemAdmin, requireFeatureOrCial, requireSubscription, requireGlobalScreeningAvailable, resolvePersonIdentifier, resolveCompanyIdentifier, requirePlatformCountry } from './guards';
import { CNPJ_ANY_REGEX } from '@/utils/validators';
import SystemAdminCnaeLists from '@/views/SystemAdmin/CnaeLists.vue';
import AccountAdminCnaeLists from '@/views/AccountAdmin/CnaeLists.vue';
import SystemAdminRiskTemplates from '@/views/SystemAdminRiskTemplates.vue';
import KycView from '@/views/KycView.vue';

import AccountAdminUserManagementView from '@/views/account-admin/UserManagementView.vue';
import SystemAdminUserManagementView from '@/views/system-admin/UserManagementView.vue';
import SystemAdminAccountManagementView from '@/views/system-admin/AccountManagementView.vue';
import SettingsView from '@/views/SettingsView.vue';
import KycPersonView from '@/views/KycPersonView.vue';

const routes: Array<RouteRecordRaw> = [
  {
    path: '/login',
    name: 'login',
    component: LoginView,
    beforeEnter: requireGuest
  },
  {
    path: '/auth/check',
    redirect: (to) => ({ name: 'login', query: to.query })
  },
  {
    path: '/subscription-required',
    name: 'subscription-required',
    component: () => import('@/views/SubscriptionRequiredView.vue'),
    beforeEnter: requireAuth,
  },
  {
    path: '/',
    component: SidePanelLayout,
    beforeEnter: [requireAuth, requireSubscription],
    children: [
      {path: '', component: HomeView},
      {path: 'demo-home', name: 'demo-home', component: () => import('@/views/demoHome/DemoHomeView.vue')},
      {path: 'demo-getting-started', name: 'demo-getting-started', component: () => import('@/views/demoGettingStarted/DemoGettingStartedView.vue')},
      {path: 'kyx', redirect: { name: 'global-kyc-dashboard' }},
      {path: 'kyx/dashboard', component: () => import('@/views/global/GlobalKycDashboardView.vue'), name: 'global-kyc-dashboard'},
      {path: 'kyx/operations', component: () => import('@/views/global/GlobalKycOperationsView.vue'), name: 'global-kyc-operations'},
      {path: 'kyx/parecers', component: () => import('@/views/global/GlobalParecerView.vue'), name: 'global-parecers'},
      {path: 'kyx/reports', component: () => import('@/views/global/GlobalKycPolicyReportsView.vue'), name: 'global-kyc-reports'},
      {path: 'kyx/policies/:riskProfileId/reports', component: () => import('@/views/global/GlobalKycPolicyReportsView.vue'), name: 'global-kyc-policy-reports'},
      {path: 'kyx/batch-upload', component: () => import('@/views/global/GlobalKycBatchListView.vue'), name: 'global-kyc-batch-list'},
      {path: 'kyx/batch-upload/new', component: () => import('@/views/global/GlobalKycBatchUploadView.vue'), name: 'global-kyc-batch-new'},
      {path: 'kyx/batch-upload/:batchId', component: () => import('@/views/global/GlobalKycBatchUploadView.vue'), name: 'global-kyc-batch-detail'},
      {path: 'kyt', component: () => import('@/views/global/GlobalKytView.vue'), name: 'global-kyt', beforeEnter: requireFeatureOrCial('kyt')},
      {path: 'coming-soon', component: () => import('@/components/common/ComingSoonView.vue'), name: 'coming-soon'},
      // Where the platform guard sends pages closed to the account's platform (or when it isn't set).
      {
        path: 'platform-unavailable',
        component: () => import('@/views/PlatformUnavailableView.vue'),
        name: 'platform-unavailable',
        meta: { platformCountries: 'any' },
      },
      {
        path: 'global-screening',
        component: () => import('@/views/global/GlobalScreeningView.vue'),
        beforeEnter: requireGlobalScreeningAvailable,
        meta: {
          comingSoon: {
            title: 'Triagem Global — em breve',
            description: 'O módulo de Triagem Global está em desenvolvimento e estará disponível em breve.',
          },
        },
        children: [
          { path: '', redirect: { name: 'global-screening' } },
          {
            path: 'search',
            name: 'global-screening',
            component: () => import('@/views/global/GlobalScreeningSearchView.vue'),
          },
          {
            path: 'history',
            name: 'global-screening-history',
            component: () => import('@/views/global/GlobalScreeningHistoryView.vue'),
          },
        ],
      },
      {
        path: 'global-screening/:applicantId',
        component: () => import('@/views/global/GlobalScreeningResultView.vue'),
        beforeEnter: requireGlobalScreeningAvailable,
        name: 'global-screening-result',
        meta: {
          comingSoon: {
            title: 'Triagem Global — em breve',
            description: 'O módulo de Triagem Global está em desenvolvimento e estará disponível em breve.',
          },
        },
      },
      {
        path: 'entity-pulse',
        component: () => import('@/views/global/GlobalEntityPulseView.vue'),
        beforeEnter: requireFeatureOrCial('pulse'),
        children: [
          { path: '', name: 'global-entity-pulse', redirect: { name: 'global-entity-pulse-policies' } },
          { path: 'events', component: () => import('@/views/global/GlobalEntityPulseEventsView.vue'), name: 'global-entity-pulse-events' },
          { path: 'entities', component: () => import('@/views/global/GlobalEntityPulseEntitiesView.vue'), name: 'global-entity-pulse-entities' },
          { path: 'policies', component: () => import('@/views/global/GlobalEntityPulsePoliciesView.vue'), name: 'global-entity-pulse-policies' },
          { path: 'runs', component: () => import('@/views/global/GlobalEntityPulseRunsView.vue'), name: 'global-entity-pulse-runs' },
          { path: 'scheduled', component: () => import('@/views/global/GlobalEntityPulseScheduledView.vue'), name: 'global-entity-pulse-scheduled' },
        ],
      },
      {path: 'alerts', component: () => import('@/views/global/GlobalAlertsView.vue'), name: 'global-alerts'},
      {path: 'alerts/:id', component: () => import('@/views/alerts/AlertDetailView.vue'), name: 'alert-details'},
      {path: 'investigations', component: () => import('@/views/investigations/InvestigationList.vue'), name: 'global-investigations'},
      {path: 'investigations/:id', component: () => import('@/views/investigations/InvestigationDetails.vue'), name: 'investigation-details'},
      {path: 'sars', component: () => import('@/views/global/GlobalSarView.vue'), name: 'global-sars'},
      {path: 'sars/:id', component: () => import('@/views/sars/SarCaseDetail.vue'), name: 'sar-details'},
      {path: 'settings', component: SettingsView, name: 'settings'},
      {path: 'account/spending', component: () => import('@/views/account/AccountSpendingSummaryView.vue'), name: 'account-spending-summary'},
      {path: 'ingestion', component: () => import('@/views/ingestion/IngestionHomeView.vue'), name: 'ingestion-home'},
      {path: 'ingestion/wizard/:category', component: () => import('@/views/ingestion/MappingWizardView.vue'), name: 'ingestion-wizard'},
      {path: 'ingestion/upload/:mappingId', component: () => import('@/views/ingestion/UploadProgressView.vue'), name: 'ingestion-upload'},
    ]
  },
  {
    path: '/account-admin',
    component: SidePanelLayout,
    beforeEnter: requireAuth,
    children: [
      {path: 'kyx', component: AdminProfiles, name:'account-admin-profiles'},
      {
        path: 'kyx/wizard',
        component: () => import('@/components/accountAdmin/configuration/risk/RiskProfileConfig.vue'),
        name: 'account-admin-profile-wizard',
      },
      {
        path: 'kyx/:id',
        component: () => import('@/components/accountAdmin/configuration/risk/ProfileVersionManager.vue'),
        name: 'account-admin-profile-details',
        props: (route) => ({ profileId: route.params.id, storeType: 'profiles' })
      },
      {path: 'relationship-types', component: RelationshipTypesView, name:'account-admin-relationship-types'},
      {path: 'transactions-platforms', component: () => import('@/views/TransactionsPlatformsView.vue'), name: 'account-admin-transactions-platforms'},
      {
        path: 'monitoring',
        name: 'account-admin-monitoring',
        component: () => import('@/components/accountAdmin/configuration/monitoring/KycMonitoringView.vue'),
      },
      {
        path: 'monitoring/:policyId',
        name: 'account-admin-monitoring-details',
        component: () => import('@/components/accountAdmin/configuration/monitoring/KycMonitoringDetailsView.vue'),
        props: (route) => ({ policyId: route.params.policyId as string, context: 'admin' }),
      },
      {
        path: 'kyt',
        name: 'account-admin-kyt',
        component: () => import('@/components/accountAdmin/configuration/kyt/KytConfigView.vue'),
        beforeEnter: requireFeatureOrCial('kyt'),
      },
      {
        path: 'kyt/:policyId',
        name: 'account-admin-kyt-details',
        component: () => import('@/components/accountAdmin/configuration/kyt/KytConfigDetailsView.vue'),
        props: (route) => ({ policyId: route.params.policyId as string, context: 'admin' }),
        beforeEnter: requireFeatureOrCial('kyt'),
      },
      {path: 'cnae-lists', component: AccountAdminCnaeLists, name:'account-admin-cnae-lists'},
      {path: 'users', component: AccountAdminUserManagementView, name:'account-admin-users'},
      {path: 'api-keys', component: () => import('@/views/account-admin/ApiUsersView.vue'), name: 'account-admin-api-keys'},
      {path: 'roles', component: () => import('@/views/account-admin/AccountRoleManagementView.vue'), name: 'account-admin-roles'},
      {path: 'regulatory-compliance', component: () => import('@/views/account-admin/RegulatoryComplianceView.vue'), name: 'account-admin-regulatory-compliance'},
      {path: 'billing', component: () => import('@/views/system-admin/AccountBillingView.vue'), name: 'account-admin-billing'},
      {path: 'data-usage', redirect: { name: 'account-admin-billing' }},
    ]
  },
  {
    path: '/system-admin',
    component: SidePanelLayout,
    beforeEnter: requireSystemAdmin,
    children: [
      {
        path: 'risk-model-templates',
        name: 'system-admin-risk-model-templates',
        component: SystemAdminRiskTemplates
      },
      {
        path: 'risk-model-templates/wizard',
        name: 'system-admin-template-wizard',
        component: () => import('@/views/SystemAdmin/TemplateConfig.vue'),
      },
      {
        path: 'risk-model-templates/:id',
        name: 'system-admin-template-details',
        component: () => import('@/components/accountAdmin/configuration/risk/ProfileVersionManager.vue'),
        props: (route) => ({ profileId: route.params.id, storeType: 'templates' })
      },
      {
        path: 'monitoring-templates',
        name: 'system-admin-monitoring-templates',
        component: () => import('@/components/accountAdmin/configuration/monitoring/KycMonitoringView.vue'),
        props: { context: 'system' }
      },
      {
        path: 'monitoring-templates/:policyId',
        name: 'system-admin-monitoring-templates-details',
        component: () => import('@/components/accountAdmin/configuration/monitoring/KycMonitoringDetailsView.vue'),
        props: (route) => ({ policyId: route.params.policyId as string, context: 'system' }),
      },
      {
        path: 'kyt-templates',
        name: 'system-admin-kyt-templates',
        component: () => import('@/components/accountAdmin/configuration/kyt/KytConfigView.vue'),
        props: { context: 'system' }
      },
      {
        path: 'kyt-templates/:policyId',
        name: 'system-admin-kyt-templates-details',
        component: () => import('@/components/accountAdmin/configuration/kyt/KytConfigDetailsView.vue'),
        props: (route) => ({ policyId: route.params.policyId as string, context: 'system' }),
      },
      {
        path: 'cnae-lists',
        name: 'system-admin-cnae-lists',
        component: SystemAdminCnaeLists
      },
      {
        path: 'relationship-types',
        name: 'system-admin-relationship-types',
        component: () => import('@/views/system-admin/SystemRelationshipTypesView.vue')
      },
      {
        path: 'users',
        name: 'system-admin-users',
        component: SystemAdminUserManagementView
      },
      {
        path: 'accounts',
        name: 'system-admin-accounts',
        component: SystemAdminAccountManagementView
      },
      {
        path: 'roles',
        name: 'system-admin-roles',
        component: () => import('@/views/system-admin/SystemAdminRoleManagementView.vue')
      },
      {
        path: 'workflow-templates',
        name: 'system-admin-workflow-templates',
        component: () => import('@/views/system-admin/WorkflowTemplatesView.vue')
      },
      {
        path: 'workflow-templates/:id',
        name: 'system-admin-workflow-template-config',
        component: () => import('@/views/system-admin/WorkflowTemplateConfigView.vue')
      },
      {
        path: 'accounts/:id/workflow',
        name: 'system-admin-account-workflow',
        component: () => import('@/views/system-admin/AccountWorkflowConfigView.vue')
      },
      {
        path: 'accounts/:id/billing',
        name: 'system-admin-account-billing',
        component: () => import('@/views/system-admin/AccountBillingView.vue')
      },
      {
        path: 'accounts/:id/kyx-pricing',
        name: 'system-admin-account-kyx-pricing',
        component: () => import('@/views/system-admin/AccountKyxPricingView.vue')
      },
      {
        path: 'data-catalog',
        name: 'system-admin-data-catalog',
        component: () => import('@/views/system-admin/DataCatalogView.vue')
      },
      {
        path: 'regulatory-mappings',
        name: 'system-admin-regulatory-mappings',
        component: () => import('@/views/system-admin/RegulatoryMappingsView.vue')
      },
      {
        // URL-only (no nav link): system-wide usage/activity observability.
        // Guarded by requireSystemAdmin like the rest of this subtree.
        path: 'observability',
        name: 'system-admin-observability',
        component: () => import('@/views/system-admin/PlatformObservabilityView.vue')
      }
    ]
  },

  {
    path: '/company-lookup',
    component: SidePanelLayout,
    beforeEnter: [requireAuth, requireSubscription],
    children: [
      {path: '', component: LookupView, name:'company-lookup', props: { type: 'company' }},
    ]
  },
  {
    path: `/company/:cnpj(${CNPJ_ANY_REGEX})`,
    component: SidePanelLayout,
    beforeEnter: [requireAuth, requireSubscription, resolveCompanyIdentifier],
    meta: { pageKeyParam: 'cnpj' },
    children: [
      {
        path: '',
        component: CompanyPageView,
        children: [
          { path: '', redirect: { name: 'company-general-headquarters' } },
          { path: 'general', redirect: { name: 'company-general-headquarters' } },
          { path: 'general/headquarters', component: () => import('@/views/CompanyGeneralData.vue'), name: 'company-general-headquarters', meta: { keepAlive: true } },
          { path: 'general/corporate-profile', component: () => import('@/views/company/CompanyCorporateProfile.vue'), name: 'company-general-corporate', meta: { keepAlive: true } },
          { path: 'general/branches', component: () => import('@/views/company/CompanyBranches.vue'), name: 'company-general-branches', meta: { keepAlive: true } },
//           { path: 'general/social-contract', component: () => import('@/views/company/CompanySocialContract.vue'), name: 'company-general-social-contract' },
          {
            path: 'general/relationship-tree',
            component: () => import('@/views/entity/EntityRelationshipTreeView.vue'),
            name: 'company-general-relationship-tree',
            props: (route) => ({
              entityType: 'company',
              entityId: route.params.cnpj as string
            })
          },
//           { path: 'general/revenue', component: () => import('@/views/company/CompanyRevenue.vue'), name: 'company-general-revenue' },
          { path: 'kyx', redirect: { name: 'company-kyc-lookup' } },
          { path: 'kyx/lookup', component: KycView, name: 'company-kyc-lookup', meta: { keepAlive: true } },
          { path: 'kyx/assessment', component: () => import('@/views/company/CompanyKycAssessment.vue'), name: 'company-kyc-assessment', meta: { keepAlive: true } },
          { path: 'kyx/parecer/:requestId', component: () => import('@/views/company/CompanyKycParecerView.vue'), name: 'company-kyc-parecer-view' },
          {
            path: 'kyx/parecer/:parecerId/review',
            component: () => import('@/views/company/ManagerReviewView.vue'),
            name: 'company-kyc-manager-review',
            meta: { title: 'Manager Review' }
          },
          {
            path: 'kyx/review/:requestId/wizard',
            component: () => import('@/views/company/CompanyKycReviewDecisionWizard.vue'),
            name: 'company-kyc-review-wizard',
            meta: { title: 'KYX Decision Wizard', hideLayout: true }
          },
          { path: 'kyt', component: () => import('@/views/company/CompanyKytView.vue'), name: 'company-kyt', meta: { keepAlive: true } },
          { path: 'alerts', component: () => import('@/views/company/CompanyAlertsView.vue'), name: 'company-alerts', meta: { keepAlive: true } },
          {
            // Named `company-alerts-...` so the Alertas tab stays active (see `isTabActive`).
            path: 'alerts/:id',
            component: () => import('@/views/alerts/AlertDetailView.vue'),
            name: 'company-alerts-detail',
            props: { entityType: 'company' },
          },
          {
            path: 'investigations',
            component: () => import('@/components/investigations/EntityInvestigationsTable.vue'),
            name: 'company-investigations',
            props: route => ({ entityId: (route.params.cnpj as string || '').slice(0, 8), entityType: 'company' }),
            meta: { keepAlive: true },
          },
          { path: 'sar', component: () => import('@/views/company/CompanySarView.vue'), name: 'company-sar', meta: { keepAlive: true } },
          {
            path: 'entity-pulse',
            component: () => import('@/views/entity/EntityPulseView.vue'),
            name: 'company-entity-pulse',
            props: (route) => ({
              entityType: 'company',
              entityId: route.params.cnpj as string,
            }),
            meta: { keepAlive: true },
          },
          { path: 'other', redirect: { name: 'company-other-novo-lawsuits' } },
          {
            path: 'other/lawsuits',
            name: 'company-other-lawsuits',
            redirect: { name: 'company-other-novo-lawsuits' },
          },
          {
            path: 'other/media-ai',
            component: () => import('@/views/entity/EntityMediaAiView.vue'),
            name: 'company-other-media-ai',
            props: (route) => ({
              entityType: 'company',
              entityId: route.params.cnpj as string,
            }),
            meta: { keepAlive: true },
          },
          {
            path: 'other/certificates',
            component: () => import('@/views/entity/EntityCertificatesView.vue'),
            name: 'company-other-certificates',
            props: (route) => ({
              entityType: 'company',
              entityId: route.params.cnpj as string,
            }),
            meta: { keepAlive: true },
          },
          {
            path: 'other/lawsuits-novo',
            component: () => import('@/views/entity/EntityPredictusLawsuitsView.vue'),
            name: 'company-other-novo-lawsuits',
            props: (route) => ({
              entityType: 'company',
              entityId: route.params.cnpj as string,
            }),
            meta: {
              keepAlive: true,
              comingSoon: {
                title: 'Processos (novo) — em breve',
                description: 'Este módulo está em desenvolvimento e estará disponível em breve.',
              },
            },
          },
          {
            path: 'other/lawsuits/:processNumber(.+)',
            name: 'company-other-lawsuit-detail',
            redirect: { name: 'company-other-lawsuit-novo-detail' },
          },
          {
            path: 'other/lawsuits-novo/:processNumber(.+)',
            component: () => import('@/views/entity/EntityPredictusLawsuitDetailView.vue'),
            name: 'company-other-lawsuit-novo-detail',
            props: (route) => ({
              entityType: 'company',
              entityId: route.params.cnpj as string,
              processNumber: route.params.processNumber as string,
            }),
            meta: {
              comingSoon: {
                title: 'Processos (novo) — em breve',
                description: 'Este módulo está em desenvolvimento e estará disponível em breve.',
              },
            },
          },
        ]
      }
    ]
  },
  {
    path: '/person-lookup',
    component: SidePanelLayout,
    beforeEnter: [requireAuth, requireSubscription],
    children: [
      {path: '',  name:'person-lookup', component: LookupView, props: { type: 'person' }},
    ]
  },
  {
    path: '/person/:uuid',
    component: SidePanelLayout,
    beforeEnter: [requireAuth, requireSubscription, resolvePersonIdentifier],
    meta: { pageKeyParam: 'uuid' },
    children: [
      {
        path: '',
        component: PersonPageView,
        children: [
          { path: '', redirect: { name: 'person-general-data' } },
          { path: 'general', component: PersonGeneralData, name: 'person-general-data', meta: { keepAlive: true } },
          {
            path: 'general/relationship-tree',
            component: () => import('@/views/entity/EntityRelationshipTreeAccessView.vue'),
            name: 'person-relationship-tree',
            props: (route) => ({
              entityType: 'person',
              entityId: route.params.uuid as string
            })
          },
          { path: 'kyx', component: KycPersonView, name: 'person-kyc', meta: { keepAlive: true } },
          { path: 'kyx/parecer/:requestId', component: () => import('@/views/company/CompanyKycParecerView.vue'), name: 'person-kyc-parecer-view' },
          {
            path: 'kyx/parecer/:parecerId/review',
            component: () => import('@/views/company/ManagerReviewView.vue'),
            name: 'person-kyc-manager-review',
            meta: { title: 'Manager Review' }
          },
          {
            path: 'kyx/review/:requestId/wizard',
            component: () => import('@/views/company/CompanyKycReviewDecisionWizard.vue'),
            name: 'person-kyc-review-wizard',
            meta: { title: 'KYX Decision Wizard', hideLayout: true }
          },
          { path: 'kyt', component: () => import('@/views/person/PersonKytView.vue'), name: 'person-kyt', meta: { keepAlive: true } },
          { path: 'alerts', component: () => import('@/views/person/PersonAlertsView.vue'), name: 'person-alerts', meta: { keepAlive: true } },
          {
            // Named `person-alerts-...` so the Alertas tab stays active (see `isTabActive`).
            path: 'alerts/:id',
            component: () => import('@/views/alerts/AlertDetailView.vue'),
            name: 'person-alerts-detail',
            props: { entityType: 'person' },
          },
          {
            path: 'investigations',
            component: () => import('@/components/investigations/EntityInvestigationsTable.vue'),
            name: 'person-investigations',
            props: route => ({ entityId: route.params.uuid as string, entityType: 'person' }),
            meta: { keepAlive: true },
          },
          { path: 'sar', component: () => import('@/views/person/PersonSarView.vue'), name: 'person-sar', meta: { keepAlive: true } },
          {
            path: 'entity-pulse',
            component: () => import('@/views/entity/EntityPulseView.vue'),
            name: 'person-entity-pulse',
            props: (route) => ({
              entityType: 'person',
              entityId: route.params.uuid as string,
            }),
            meta: { keepAlive: true },
          },
          { path: 'other', redirect: { name: 'person-other-novo-lawsuits' } },
          {
            path: 'other/certificates',
            name: 'person-other-certificates',
            component: () => import('@/views/person/PersonCertificatesView.vue'),
            props: (route) => ({ entityId: route.params.uuid as string }),
          },
          {
            path: 'other/lawsuits',
            name: 'person-other-lawsuits',
            redirect: { name: 'person-other-novo-lawsuits' },
          },
          {
            path: 'other/media-ai',
            component: () => import('@/views/person/PersonMediaAiView.vue'),
            name: 'person-other-media-ai',
            props: (route) => ({
              entityId: route.params.uuid as string,
            }),
            meta: { keepAlive: true },
          },
          {
            path: 'other/lawsuits-novo',
            component: () => import('@/views/entity/EntityPredictusLawsuitsView.vue'),
            name: 'person-other-novo-lawsuits',
            props: (route) => ({
              entityType: 'person',
              entityId: route.params.uuid as string,
            }),
            meta: {
              keepAlive: true,
              comingSoon: {
                title: 'Processos — em breve',
                description: 'Este módulo está em desenvolvimento e estará disponível em breve.',
              },
            },
          },
          {
            path: 'other/lawsuits/:processNumber(.+)',
            name: 'person-other-lawsuit-detail',
            redirect: { name: 'person-other-lawsuit-novo-detail' },
          },
          {
            path: 'other/lawsuits-novo/:processNumber(.+)',
            component: () => import('@/views/entity/EntityPredictusLawsuitDetailView.vue'),
            name: 'person-other-lawsuit-novo-detail',
            props: (route) => ({
              entityType: 'person',
              entityId: route.params.uuid as string,
              processNumber: route.params.processNumber as string,
            }),
            meta: {
              comingSoon: {
                title: 'Processos — em breve',
                description: 'Este módulo está em desenvolvimento e estará disponível em breve.',
              },
            },
          },
        ]
      }
    ]
  },
  {
    path: '/home',
    name: 'home',
    component: HomeView,
    beforeEnter: [requireAuth, requireSubscription],
  },
  {
    path: '/about',
    name: 'about',
    beforeEnter: [requireAuth, requireSubscription],
    // route level code-splitting
    // this generates a separate chunk (About.[hash].js) for this route
    // which is lazy-loaded when the route is visited.
    component: () => import('../views/AboutView.vue'),
  },
  {
    path: '/dev/risk-icons-demo',
    name: 'risk-icons-demo',
    component: () => import('@/components/company-kyc/RiskLevelIconsDemo.vue'),
    beforeEnter: [requireAuth, requireSubscription],
  },
  {
    path: '/marketing/preview',
    name: 'marketing-preview',
    beforeEnter: requireSystemAdmin,
    component: () => import('@/views/system-admin/MarketingPreviewView.vue'),
    meta: { title: 'Site de marketing' },
  },
  {
    path: '/system-admin/marketing/preview',
    redirect: '/marketing/preview',
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'NotFound',
    component: () => import('@/views/NotFound.vue'),
    meta: { platformCountries: 'any' },
  }
];

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
  scrollBehavior(_to, _from, savedPosition) {
    const scrollContainer = document.querySelector('.pv-content');
    if (scrollContainer) {
      scrollContainer.scrollTo(0, 0);
    } else {
      window.scrollTo(0, 0);
    }
    if (savedPosition) {
      return savedPosition;
    }
    return { top: 0 };
  },
});

router.beforeEach(requirePlatformCountry);

// Handle dynamic import failures (e.g., when a new version is deployed and old chunks are gone)
router.onError((error, to) => {
  const isChunkLoadFailed = error.message.includes('Failed to fetch dynamically imported module') ||
                            error.message.includes('Importing a module script failed') ||
                            error.name === 'ChunkLoadError';

  if (isChunkLoadFailed) {
    const targetPath = to.fullPath;
    const storageKey = `router-reload-${targetPath}`;
    const lastRetry = sessionStorage.getItem(storageKey);
    const now = Date.now();

    // Prevent infinite loops: only reload if we haven't tried in the last 10 seconds
    if (!lastRetry || now - parseInt(lastRetry) > 10000) {
      sessionStorage.setItem(storageKey, now.toString());

      // Force reload with cache-busting query param to ensure we get the new index.html
      const url = new URL(window.location.href);
      url.searchParams.set('reload_ts', now.toString());
      window.location.assign(url.toString());
    } else {
      console.error('Chunk load failed even after reload:', error);
    }
  }
});

export default router;
